import { useState } from "react";
import { Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { postForm } from "@/lib/api";
import { postJson } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { COUNTRY_CODES, TURTLES, countryName, flagOf } from "@/data/profile";
import { Avatar } from "@/components/community/shared";

const L = ({ label, children }) => (
  <label className="block">
    <span className="mono mb-2 block text-[11px] uppercase tracking-[0.18em] text-[var(--muted)]">{label}</span>
    {children}
  </label>
);

export const ProfileEditor = ({ member, onSaved, open, setOpen }) => {
  const { t, lang } = useLang();
  const [v, setV] = useState({
    name: member.name || "", country: member.country || "", fan_since: member.fan_since ?? "",
    favorite: member.favorite || "", bio: member.bio || "", collection_public: member.collection_public !== false,
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: e.target.value }));
  const countries = COUNTRY_CODES.map((c) => [c, countryName(c, lang)]).sort((a, b) => a[1].localeCompare(b[1], lang));
  const uploadAvatar = async (file) => {
    if (!file) return;
    try {
      onSaved(await postForm("/members/me/avatar", { file }));
      toast.success(t("prof.photoSaved"));
    } catch (e) {
      toast.error(e.message);
    }
  };
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      onSaved(await postJson("/members/me", { ...v, fan_since: v.fan_since ? Number(v.fan_since) : null }, "PATCH"));
      toast.success(t("prof.saved"));
      setOpen(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto border-white/10 bg-[#15130f] text-[#efe8dc]" data-lenis-prevent data-testid="profile-editor">
        <DialogTitle className="font-display text-3xl font-normal">{t("prof.edit")}</DialogTitle>
        <form onSubmit={save} className="mt-2 space-y-4" data-testid="profile-editor-form">
          <div className="flex items-center gap-4">
            <Avatar author={member} size={64} />
            <label className="btn-ghost !py-2 cursor-pointer" data-testid="profile-avatar-upload">
              <Camera size={15} /> {t("prof.changePhoto")}
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => uploadAvatar(e.target.files?.[0])} />
            </label>
          </div>
          <L label={t("form.name")}><input value={v.name} onChange={set("name")} required minLength={2} maxLength={60} className="support-input" data-testid="profile-name-input" /></L>
          <div className="grid gap-4 sm:grid-cols-2">
            <L label={t("prof.country")}>
              <select value={v.country} onChange={set("country")} className="support-input" data-testid="profile-country-select">
                <option value="">—</option>
                {countries.map(([c, n]) => <option key={c} value={c}>{flagOf(c)} {n}</option>)}
              </select>
            </L>
            <L label={t("prof.fanSinceLabel")}><input type="number" min={1984} max={new Date().getFullYear()} value={v.fan_since} onChange={set("fan_since")} placeholder="1990" className="support-input" data-testid="profile-fan-since-input" /></L>
          </div>
          <L label={t("prof.favorite")}>
            <div className="flex flex-wrap gap-2">
              {Object.entries(TURTLES).map(([k, x]) => (
                <button key={k} type="button" onClick={() => setV((s) => ({ ...s, favorite: s.favorite === k ? "" : k }))} className="col-chip" style={v.favorite === k ? { background: x.color, borderColor: x.color, color: "#0d0c0a" } : undefined} data-testid={`profile-favorite-${k}`}>{x.name}</button>
              ))}
            </div>
          </L>
          <L label={t("prof.bioLabel")}><textarea value={v.bio} onChange={set("bio")} maxLength={2000} rows={6} placeholder={t("prof.bioPh")} className="support-input resize-none" data-testid="profile-bio-input" /></L>
          <label className="flex items-center justify-between gap-4 text-sm">
            {t("prof.publicCollection")}
            <Switch checked={v.collection_public} onCheckedChange={(c) => setV((s) => ({ ...s, collection_public: c }))} data-testid="profile-public-switch" />
          </label>
          <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="profile-editor-save">{busy && <Loader2 size={15} className="animate-spin" />} {t("profile.save")}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
